import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  Paper,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  ShoppingCart as CartIcon,
  Visibility as ViewIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  History as HistoryIcon
} from '@mui/icons-material'
import { searchService, EnhancedSearchResult, SearchStats } from '../services/searchService'

interface SearchComponentProps {
  onProductSelect?: (product: EnhancedSearchResult) => void
  onRequestPrice?: (product: EnhancedSearchResult) => void
  showStats?: boolean
  maxResults?: number
  searchSource?: 'firestore' | 'google' | 'hybrid'
}

export default function SearchComponent({ 
  onProductSelect, 
  onRequestPrice,
  showStats = true,
  maxResults = 20,
  searchSource = 'hybrid'
}: SearchComponentProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<EnhancedSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [stats, setStats] = useState<SearchStats | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [searchHistory, setSearchHistory] = useState<string[]>([])
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Load search history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('karsal_search_history')
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory))
    }
  }, [])

  // Save search history to localStorage
  const saveToHistory = (searchQuery: string) => {
    if (!searchQuery.trim()) return
    
    const newHistory = [searchQuery, ...searchHistory.filter(h => h !== searchQuery)].slice(0, 10)
    setSearchHistory(newHistory)
    localStorage.setItem('karsal_search_history', JSON.stringify(newHistory))
  }

  // Handle search
  const handleSearch = async (searchQuery: string = query, page: number = 1) => {
    if (!searchQuery.trim()) return

    setLoading(true)
    setError('')
    setShowSuggestions(false)

    try {
      const response = await searchService.search(searchQuery, {
        start: (page - 1) * 10 + 1,
        num: maxResults,
        enableAICategorization: true,
        searchSource: searchSource
      })

      if (page === 1) {
        setResults(response.results)
        saveToHistory(searchQuery)
      } else {
        setResults(prev => [...prev, ...response.results])
      }

      setStats(response.stats)
      setHasMore(response.hasMore)
      setCurrentPage(page)
    } catch (err) {
      setError('خطأ في البحث. يرجى المحاولة مرة أخرى.')
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  // Handle input change with suggestions
  const handleInputChange = (value: string) => {
    setQuery(value)
    
    if (value.length > 2) {
      // Generate suggestions based on history and common searches
      const filteredHistory = searchHistory.filter(h => 
        h.toLowerCase().includes(value.toLowerCase())
      )
      setSuggestions(filteredHistory.slice(0, 5))
      setShowSuggestions(true)
    } else {
      setShowSuggestions(false)
    }
  }

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
    setShowSuggestions(false)
    handleSearch(suggestion)
  }

  // Handle clear search
  const handleClear = () => {
    setQuery('')
    setResults([])
    setError('')
    setStats(null)
    setShowSuggestions(false)
    searchInputRef.current?.focus()
  }

  // Handle load more
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      handleSearch(query, currentPage + 1)
    }
  }

  // Handle category filter
  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category)
    const filteredResults = results.filter(r => 
      !category || r.category === category
    )
    setResults(filteredResults)
  }

  // Get unique categories from results
  const getUniqueCategories = () => {
    const categories = results.map(r => r.category).filter(Boolean)
    const uniqueCategories = [...new Set(categories)]
    
    // If no categories from results, use standard categories
    if (uniqueCategories.length === 0) {
      return [
        'موبايل وتابلت', 'تلفزيونات', 'أجهزة كبيرة', 'أجهزة صغيرة',
        'أجهزة المطبخ', 'كمبيوتر', 'الصحة والجمال', 'إلكترونيات',
        'ادوات المنزل', 'ملابس'
      ]
    }
    
    return uniqueCategories
  }

  // Format price
  const formatPrice = (price: number) => {
    if (price === 0) return ''
    return `${price.toLocaleString()} ج.م`
  }

  // Get category color
  const getCategoryColor = (category: string) => {
    const colors = {
      'موبايل وتابلت': 'primary',
      'تلفزيونات': 'secondary',
      'أجهزة كبيرة': 'success',
      'أجهزة صغيرة': 'info',
      'أجهزة المطبخ': 'warning',
      'كمبيوتر': 'error',
      'الصحة والجمال': 'default'
    } as const
    
    return colors[category as keyof typeof colors] || 'default'
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 2 }}>
      {/* Search Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <SearchIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1" color="primary">
            البحث في المنتجات
          </Typography>
        </Box>
        
        {/* Search Input */}
        <Box position="relative">
          <TextField
            ref={searchInputRef}
            fullWidth
            variant="outlined"
            placeholder="ابحث عن أي منتج في السوق المصري..."
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: query && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClear} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '1.1rem'
              }
            }}
          />

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <Paper
              ref={suggestionsRef}
              elevation={3}
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                mt: 1,
                maxHeight: 200,
                overflow: 'auto'
              }}
            >
              {suggestions.map((suggestion, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon>
                    <HistoryIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={suggestion} />
                </MenuItem>
              ))}
            </Paper>
          )}
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={2} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'جاري البحث...' : 'بحث'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterIcon />}
            sx={{ borderRadius: 2 }}
          >
            فلاتر
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => searchService.clearCaches()}
            startIcon={<RefreshIcon />}
            sx={{ borderRadius: 2 }}
          >
            تحديث
          </Button>
        </Box>

        {/* Filters */}
        {showFilters && (
          <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              الفلترة حسب الفئة:
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                label="الكل"
                onClick={() => handleCategoryFilter('')}
                color={selectedCategory === '' ? 'primary' : 'default'}
                variant={selectedCategory === '' ? 'filled' : 'outlined'}
              />
              {getUniqueCategories().map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => handleCategoryFilter(category || '')}
                  color={selectedCategory === category ? 'primary' : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search Stats */}
      {showStats && stats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              إحصائيات البحث
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">
                  إجمالي النتائج
                </Typography>
                <Typography variant="h6" color="primary">
                  {stats.totalResults.toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">
                  النتائج المعروضة
                </Typography>
                <Typography variant="h6" color="success.main">
                  {stats.processedResults}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">
                  تم تصنيفها بالذكاء الاصطناعي
                </Typography>
                <Typography variant="h6" color="info.main">
                  {stats.aiCategorizations}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">
                  وقت البحث
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {stats.searchTime}ms
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <Box>
          <Typography variant="h5" gutterBottom>
            نتائج البحث ({results.length})
          </Typography>
          
          <Grid container spacing={3}>
            {results.map((result, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  {/* Product Image */}
                  <Box
                    sx={{
                      height: 200,
                      backgroundImage: result.imageUrl ? `url(${result.imageUrl})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundColor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {!result.imageUrl && (
                      <Typography color="textSecondary">
                        لا توجد صورة
                      </Typography>
                    )}
                  </Box>

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Product Title */}
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom
                      sx={{ 
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {result.title}
                    </Typography>

                    {/* Product Description */}
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {result.snippet}
                    </Typography>

                    {/* Product Info */}
                    <Box sx={{ mb: 2 }}>
                      {result.brand && (
                        <Chip 
                          label={result.brand} 
                          size="small" 
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                      
                      {result.category && (
                        <Chip 
                          label={result.category} 
                          size="small" 
                          color={getCategoryColor(result.category) as any}
                          icon={<CategoryIcon />}
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                      
                      {result.price && result.price > 0 && (
                        <Chip 
                          label={formatPrice(result.price)} 
                          size="small" 
                          color="success"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )}
                    </Box>

                    {/* Source and AI Processing Info */}
                    <Box sx={{ mb: 2 }}>
                      {result.source && (
                        <Typography variant="caption" color="textSecondary" sx={{ mr: 2 }}>
                          📍 {result.source === 'firestore' ? 'قاعدة البيانات' : 
                               result.source === 'google' ? 'Google' : 'مختلط'}
                        </Typography>
                      )}
                      {result.isProcessed && (
                        <Typography variant="caption" color="info.main">
                          🤖 تم تصنيفها بالذكاء الاصطناعي
                          {result.categoryConfidence && (
                            ` (${Math.round(result.categoryConfidence * 100)}%)`
                          )}
                        </Typography>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box display="flex" gap={1} mt="auto">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => onProductSelect?.(result)}
                        sx={{ flex: 1 }}
                      >
                        عرض التفاصيل
                      </Button>
                      
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CartIcon />}
                        onClick={() => onRequestPrice?.(result)}
                        sx={{ flex: 1 }}
                      >
                        طلب عرض سعر
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Load More Button */}
          {hasMore && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <TrendingIcon />}
                sx={{ px: 4, py: 1.5 }}
              >
                {loading ? 'جاري التحميل...' : 'تحميل المزيد'}
              </Button>
            </Box>
          )}
        </Box>
      )}

          {/* No Results */}
          {!loading && query && results.length === 0 && !error && (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  لم يتم العثور على نتائج
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  جرب البحث بكلمات مختلفة أو تحقق من الإملاء
                </Typography>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>نصائح:</strong> جرب البحث بكلمات مثل "شاومي" أو "موبايل" أو "سامسونج"
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}
    </Box>
  )
}
